import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, FileText, Sparkles, Heart, MessageCircle, Repeat2,
  TrendingUp, AlertTriangle, CheckCircle2, ListChecks, RefreshCw, Archive, Lock, ArrowLeft,
} from "lucide-react";
import api, { formatError } from "@/lib/api";
import { useProjects } from "@/context/ProjectContext";
import { Button } from "@/components/ui/button";
import NoProject from "@/components/NoProject";
import JourneyBar from "@/components/JourneyBar";
import { KrProgress } from "@/components/goals/KrProgress";
import { toast } from "sonner";

const METRIC_META = {
  likes: { label: "الإعجابات", icon: Heart, color: "text-rose-400" },
  comments: { label: "التعليقات", icon: MessageCircle, color: "text-sky-400" },
  reposts: { label: "إعادة النشر", icon: Repeat2, color: "text-accent" },
};

function pctColor(pct) {
  if (pct >= 90) return "text-emerald-400";
  if (pct >= 60) return "text-amber-400";
  return "text-rose-400";
}

const postMeasured = (m) => !!m && (m.submitted === true || !!((m.likes || 0) + (m.comments || 0) + (m.reposts || 0)));
const postComplete = (p) => !!p.content_id && !!p.uploaded && postMeasured(p.metrics);

export default function Measurement() {
  const { activeProject } = useProjects();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [measure, setMeasure] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reporting, setReporting] = useState(false);
  const [migrating, setMigrating] = useState(false);

  const loadMeasure = useCallback(() => {
    if (!activeProject) return;
    api.get(`/projects/${activeProject.id}/measurement`)
      .then((r) => { setMeasure(r.data); if (r.data.report) setReport(r.data.report); })
      .catch(() => {});
  }, [activeProject]);

  useEffect(() => {
    if (!activeProject) { setLoading(false); return; }
    setLoading(true); setReport(null);
    api.get(`/campaigns?project_id=${activeProject.id}`)
      .then((r) => setCampaigns(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    loadMeasure();
  }, [activeProject, loadMeasure]);

  const genReport = async () => {
    setReporting(true);
    try {
      const { data } = await api.post(`/projects/${activeProject.id}/measurement/report`);
      setReport(data.report);
      setMeasure((prev) => ({ ...prev, actuals: data.actuals, achievements: data.achievements }));
      toast.success("تم توليد التقرير التنفيذي والخطة التصحيحية 📊");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setReporting(false); }
  };

  const startNextCycle = async () => {
    setMigrating(true);
    try {
      const { data } = await api.post(`/projects/${activeProject.id}/goals/next-cycle`);
      toast.success("تم اقتراح أهداف الدورة القادمة — راجعها واعتمدها ✨");
      navigate("/app/goals", { state: { proposed: data } });
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    } finally { setMigrating(false); }
  };

  const archiveProject = async () => {
    if (!window.confirm("سيتم أرشفة المشروع (للقراءة فقط). يمكنك فتحه لاحقاً أو بدء دورة جديدة. متابعة؟")) return;
    try {
      await api.put(`/projects/${activeProject.id}/archive`, { uploaded: true });
      toast.success("تم إقفال المشروع وأرشفته ✅");
      navigate("/app/projects");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail));
    }
  };

  if (!activeProject) return <NoProject message="اختر مشروعاً لعرض توصياته وإقفال دورته." />;

  if (activeProject.standalone) {
    return (
      <div className="glass rounded-2xl p-12 text-center border border-border max-w-xl mx-auto" data-testid="strategy-only-guard">
        <Lock className="w-10 h-10 text-primary mx-auto mb-4" />
        <h3 className="text-xl font-bold mb-2">هذا القسم لمشاريع الاستراتيجية</h3>
        <p className="text-muted-foreground mb-6">«{activeProject.name}» مشروع محتوى. التوصيات والإغلاق متاحة لمشاريع الاستراتيجية فقط.</p>
        <Button onClick={() => navigate("/app/studio")} className="bg-primary hover:bg-primary/90 text-white gap-2" data-testid="guard-go-studio"><Sparkles className="w-4 h-4" /> الانتقال لاستوديو المحتوى</Button>
      </div>
    );
  }

  const hasGoals = measure?.goals;
  const achievements = measure?.achievements || [];
  const allPosts = campaigns.flatMap((c) => c.posts || []);
  const completedCount = allPosts.filter(postComplete).length;
  const totalPosts = allPosts.length;
  const executionComplete = totalPosts > 0 && completedCount === totalPosts;

  return (
    <div className="space-y-6" data-testid="measurement-page">
      <JourneyBar current="measure" />

      <div>
        <h1 className="text-3xl font-extrabold font-heading">التوصيات والإغلاق</h1>
        <p className="text-muted-foreground">راجِع تحقّق أهداف «{activeProject.name}» وولّد التوصيات ثم أقفل الدورة</p>
      </div>

      {loading ? (
        <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>
      ) : (
        <>
          {!executionComplete && (
            <div className="glass rounded-2xl p-5 border border-accent/30 bg-accent/5 flex items-center justify-between gap-4 flex-wrap" data-testid="execution-incomplete">
              <div className="flex items-center gap-3">
                <Lock className="w-5 h-5 text-accent shrink-0" />
                <p className="text-sm">
                  أكمِل تنفيذ كل المنشورات أولاً (توليد + رفع + إدخال الأداء). <b>{completedCount}/{totalPosts}</b> مكتمل حتى الآن.
                </p>
              </div>
              <Button size="sm" variant="outline" className="border-accent/40 text-accent gap-1" onClick={() => navigate("/app/campaigns")} data-testid="back-to-execution-btn">
                العودة للتنفيذ <ArrowLeft className="w-4 h-4" />
              </Button>
            </div>
          )}

          {!hasGoals && (
            <div className="glass rounded-2xl p-5 border border-amber-500/30 bg-amber-500/5 flex items-center justify-between gap-4 flex-wrap" data-testid="measure-no-goals">
              <p className="text-sm">لم تعتمد أهدافاً بعد — اعتمِد الأهداف أولاً لقياس نسب التحقّق.</p>
              <Button size="sm" variant="outline" className="border-accent/40 text-accent" onClick={() => navigate("/app/goals")}>تحديد الأهداف</Button>
            </div>
          )}

          {hasGoals && (
            <KrProgress projectId={activeProject.id} items={measure?.key_results || []} onUpdated={(krs) => setMeasure((m) => ({ ...m, key_results: krs }))} />
          )}

          {/* Achievement cards */}
          {hasGoals && (
            <div className="grid gap-4 sm:grid-cols-3" data-testid="achievement-cards">
              {achievements.map((a, idx) => {
                const meta = METRIC_META[a.metric] || METRIC_META.likes;
                return (
                  <div key={a.metric || idx} className="glass rounded-2xl p-5 border border-border" data-testid={`achievement-${a.metric}`}>
                    <div className={`flex items-center gap-2 mb-2 font-bold ${meta.color}`}>
                      <meta.icon className="w-5 h-5" /> {a.label}
                    </div>
                    <div className="flex items-end justify-between mb-2">
                      <div>
                        <div className="text-2xl font-extrabold">{a.actual}<span className="text-sm text-muted-foreground">{a.unit}</span></div>
                        <div className="text-xs text-muted-foreground">الهدف: {a.target}{a.unit}</div>
                      </div>
                      <div className={`text-2xl font-extrabold ${pctColor(a.pct)}`}>{a.pct}%</div>
                    </div>
                    <div className="h-2 rounded-full bg-secondary overflow-hidden">
                      <motion.div initial={{ width: 0 }} animate={{ width: `${Math.min(a.pct, 100)}%` }}
                        className="h-full bg-primary rounded-full" />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Report trigger — gated on execution completion */}
          {hasGoals && (
            <div className="flex flex-wrap gap-3 items-center">
              <Button onClick={genReport} disabled={reporting || !executionComplete}
                className="bg-accent hover:bg-accent/90 text-accent-foreground gap-2 font-bold disabled:opacity-50" data-testid="gen-report-btn">
                {reporting ? <Loader2 className="w-4 h-4 animate-spin" /> : executionComplete ? <Sparkles className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                توليد التقرير التنفيذي والخطة التصحيحية
              </Button>
              {!executionComplete && <span className="text-xs text-muted-foreground">يُفتح بعد اكتمال تنفيذ كل المنشورات.</span>}
            </div>
          )}

          {report && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="space-y-4" data-testid="measure-report">
              <div className="glass rounded-2xl p-6 border border-primary/30">
                <div className="flex items-center gap-2 mb-2">
                  <FileText className="w-5 h-5 text-primary" />
                  <h2 className="text-lg font-bold">الملخص التنفيذي</h2>
                  {report.overall_status && <span className="text-xs bg-primary/15 text-primary px-3 py-1 rounded-full ms-auto">{report.overall_status}</span>}
                </div>
                <p className="text-sm leading-relaxed text-muted-foreground whitespace-pre-line">{report.summary}</p>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                {report.wins?.length > 0 && (
                  <ReportList title="أبرز الإنجازات" items={report.wins} icon={CheckCircle2} color="text-emerald-400" bg="bg-emerald-500/10 border-emerald-500/20" testid="report-wins" />
                )}
                {report.gaps?.length > 0 && (
                  <ReportList title="فجوات الأداء" items={report.gaps} icon={AlertTriangle} color="text-amber-400" bg="bg-amber-500/10 border-amber-500/20" testid="report-gaps" />
                )}
              </div>

              {report.corrective_plan?.length > 0 && (
                <ReportList title="الخطة التصحيحية للدورة القادمة" items={report.corrective_plan} icon={ListChecks} color="text-primary" bg="bg-primary/5 border-primary/20" testid="report-corrective" ordered />
              )}

              {report.next_targets_hint && (
                <div className="glass rounded-2xl p-5 border border-accent/30 bg-accent/5">
                  <div className="flex items-center gap-2 mb-1 font-bold text-accent"><TrendingUp className="w-5 h-5" /> توصية لأهداف الدورة القادمة</div>
                  <p className="text-sm text-muted-foreground">{report.next_targets_hint}</p>
                </div>
              )}

              <div className="glass rounded-2xl p-5 border border-border flex items-center justify-between gap-4 flex-wrap" data-testid="close-project-row">
                <div>
                  <h3 className="font-bold mb-1 flex items-center gap-2"><Archive className="w-5 h-5 text-muted-foreground" /> إنهاء الدورة</h3>
                  <p className="text-xs text-muted-foreground">رحّل الأهداف لدورة جديدة، أو أقفل المشروع وأرشفه (يبقى للقراءة ويمكن فتحه لاحقاً).</p>
                </div>
                <div className="flex gap-2">
                  <Button onClick={startNextCycle} disabled={migrating} className="bg-primary hover:bg-primary/90 text-white gap-1" data-testid="close-next-cycle-btn">
                    {migrating ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} دورة جديدة
                  </Button>
                  <Button onClick={archiveProject} variant="outline" className="border-border gap-1" data-testid="archive-project-btn">
                    <Archive className="w-4 h-4" /> إقفال المشروع
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </>
      )}
    </div>
  );
}

function ReportList({ title, items, icon: Icon, color, bg, testid, ordered }) {
  return (
    <div className={`rounded-2xl p-5 border ${bg}`} data-testid={testid}>
      <div className={`flex items-center gap-2 mb-3 font-bold ${color}`}><Icon className="w-5 h-5" /> {title}</div>
      <ul className="space-y-2">
        {items.map((it, i) => (
          <li key={i} className="text-sm flex gap-2">
            <span className={`${color} font-bold`}>{ordered ? `${i + 1}.` : "•"}</span> {it}
          </li>
        ))}
      </ul>
    </div>
  );
}
